import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { useJobillaOrgId } from '@/hooks'
import { deployedBuildQueryOptions } from '@/lib/app-version'
import { useAuth } from '@/lib/auth/use-auth'
import { useDebugOverride } from '@/lib/debug/overrides'
import { type EchoConnectionState, useEchoConnectionState } from '@/lib/echo'
import {
	invoiceSettingsShowQueryKey,
	type OrganisationSettingsUpdateRequest,
	role as roleEnum,
	RoleEnumKey,
	useInvoiceSettingsShow,
	useOrganisationSettingsUpdate,
} from '@jobilla/nexus-sdk'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDistanceStrict } from 'date-fns'
import { PencilIcon, RotateCcwIcon } from 'lucide-react'
import type { EarlyAccessFeature } from 'posthog-js'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useState } from 'react'

type DebugModalProps = {
	isOpen: boolean
	onClose: () => void
}

const ROLE_OPTIONS: ReadonlyArray<RoleEnumKey> = Object.values(roleEnum)

export function DebugModal({ isOpen, onClose }: DebugModalProps) {
	const auth = useAuth()
	const organisationId = useJobillaOrgId()
	const queryClient = useQueryClient()
	const { value: override, setValue: setOverride } = useDebugOverride('role')
	const [isEditingRole, setIsEditingRole] = useState(false)
	const reverbConnectionState = useEchoConnectionState()

	const { data: invoiceSettings } = useInvoiceSettingsShow(organisationId, {
		enabled: !!organisationId && auth.can('invoice:view'),
		staleTime: 5 * 60 * 1000,
	})

	// Local-only endpoint that writes org settings straight to the row.
	const { mutate: updateSettings, isPending: isSavingSettings } = useOrganisationSettingsUpdate({
		onSuccess: (updated) => {
			queryClient.setQueryData(invoiceSettingsShowQueryKey(organisationId), updated)
		},
	})

	// The write endpoint only exists in local, so editing is gated to it too.
	const canEditSettings = import.meta.env.VITE_ENABLE_DEBUG_BAR === 'true'

	const setSetting = (
		field: keyof OrganisationSettingsUpdateRequest,
		next: boolean,
	) => {
		if (!organisationId) { return }
		updateSettings({ organisation: organisationId, data: { [field]: next } })
	}

	const rows: Array<[string, string | null]> = [
		['User ID', auth.user?.id ?? null],
		['Email', auth.user?.email ?? null],
	]

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="sm:max-w-xl gap-0 border-0 bg-white p-0 shadow-xl">
				<div className="flex items-center border-b border-grey-200 px-5 py-3">
					<DialogTitle className="text-base font-semibold text-grey-900">
						Debug
					</DialogTitle>
				</div>

				<dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 px-5 py-4 text-sm font-mono">
					{rows.map(([key, value]) => (
						<div key={key} className="contents">
							<dt className="text-grey-500">{key}</dt>
							<dd className="break-all text-grey-900">
								{value ?? <span className="text-grey-400">—</span>}
							</dd>
						</div>
					))}
					<dt className="text-grey-500">Role</dt>
					<dd className="break-all text-grey-900">
						{isEditingRole
							? (
								<RoleEditor
									current={auth.role ?? undefined}
									onSelect={(next) => {
										setOverride(next)
										setIsEditingRole(false)
									}}
									onCancel={() => setIsEditingRole(false)}
								/>
							)
							: (
								<div className="flex items-center gap-2">
									<span>{auth.role ?? <span className="text-grey-400">—</span>}</span>
									{override && (
										<span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-sans font-medium tracking-wide uppercase text-amber-800">
											overridden
										</span>
									)}
									<button
										type="button"
										onClick={() => setIsEditingRole(true)}
										className="cursor-pointer text-grey-400 hover:text-grey-700"
										aria-label="Override role"
										title="Override role (stored in localStorage)"
									>
										<PencilIcon className="h-3.5 w-3.5" />
									</button>
									{override && (
										<button
											type="button"
											onClick={() => setOverride(undefined)}
											className="cursor-pointer text-grey-400 hover:text-grey-700"
											aria-label="Reset role override"
											title="Reset to real role"
										>
											<RotateCcwIcon className="h-3.5 w-3.5" />
										</button>
									)}
								</div>
							)}
					</dd>
					<BooleanSettingRow
						label="Invoicing Enabled"
						value={invoiceSettings?.canPayViaInvoice}
						disabled={!canEditSettings || isSavingSettings}
						onSelect={(next) => setSetting('invoicingEnabled', next)}
					/>
					<BooleanSettingRow
						label="External Invoicing"
						value={invoiceSettings?.externalInvoicing}
						disabled={!canEditSettings || isSavingSettings}
						onSelect={(next) => setSetting('externalInvoicing', next)}
					/>
					<BooleanSettingRow
						label="Demo"
						value={invoiceSettings?.isDemo}
						disabled={!canEditSettings || isSavingSettings}
						onSelect={(next) => setSetting('isDemo', next)}
					/>
					<BooleanSettingRow
						label="Career Site Enabled"
						value={invoiceSettings?.careerSiteEnabled}
						disabled={!canEditSettings || isSavingSettings}
						onSelect={(next) => setSetting('careerSiteEnabled', next)}
					/>
				</dl>

				<ReverbStatusSection state={reverbConnectionState} />

				<ReleaseSection />

				<EarlyAccessSection />

				{auth.permissions.length > 0 && (
					<details className="border-t border-grey-200 px-5 py-3 text-sm">
						<summary className="cursor-pointer text-grey-500">
							Permissions ({auth.permissions.length})
						</summary>
						<ul className="mt-2 max-h-48 overflow-y-auto font-mono text-xs text-grey-700">
							{auth.permissions.map((permission) => <li key={permission}>{permission}</li>)}
						</ul>
					</details>
				)}
			</DialogContent>
		</Dialog>
	)
}

function EarlyAccessSection() {
	const posthog = usePostHog()
	const [features, setFeatures] = useState<EarlyAccessFeature[] | null>(null)

	const reload = () => {
		posthog.getEarlyAccessFeatures((f) => setFeatures(f), true)
	}

	useEffect(() => {
		posthog.getEarlyAccessFeatures((f) => setFeatures(f), true)
	}, [posthog])

	return (
		<details className="border-t border-grey-200 px-5 py-3 text-sm" open>
			<summary className="cursor-pointer text-grey-500">Early Access</summary>
			<div className="mt-2">
				{features === null && <p className="font-mono text-xs text-grey-400">Loading…</p>}
				{features !== null && features.length === 0 && (
					<p className="font-mono text-xs text-grey-400">No early access features found in PostHog.</p>
				)}
				{features !== null && features.length > 0 && (
					<ul className="space-y-1.5">
						{features.map((feature) => {
							if (!feature.flagKey) { return null }
							const enrolled = posthog.isFeatureEnabled(feature.flagKey) ?? false
							return (
								<li
									key={feature.flagKey}
									className="flex items-center justify-between gap-4 font-mono text-xs text-grey-700"
								>
									<span>{feature.name || feature.flagKey}</span>
									<button
										type="button"
										onClick={() => {
											posthog.updateEarlyAccessFeatureEnrollment(feature.flagKey!, !enrolled)
											reload()
										}}
										className={`cursor-pointer rounded px-2 py-0.5 text-[10px] font-sans font-medium uppercase tracking-wide ${
											enrolled
												? 'bg-sky-100 text-sky-700 hover:bg-sky-200'
												: 'bg-grey-100 text-grey-500 hover:bg-grey-200'
										}`}
									>
										{enrolled ? 'Enrolled' : 'Not enrolled'}
									</button>
								</li>
							)
						})}
					</ul>
				)}
			</div>
		</details>
	)
}

type RoleEditorProps = {
	current: RoleEnumKey | undefined
	onSelect: (role: RoleEnumKey | undefined) => void
	onCancel: () => void
}

function getRoleButtonClass(isCurrent: boolean): string {
	return isCurrent
		? 'cursor-pointer rounded border px-2 py-0.5 text-xs border-blue-500 bg-blue-50 text-blue-700'
		: 'cursor-pointer rounded border px-2 py-0.5 text-xs border-grey-300 bg-white text-grey-700 hover:border-grey-500'
}

function RoleEditor({ current, onSelect, onCancel }: RoleEditorProps) {
	return (
		<div className="flex flex-wrap items-center gap-2">
			{ROLE_OPTIONS.map((role) => (
				<button
					key={role}
					type="button"
					onClick={() => onSelect(role)}
					className={getRoleButtonClass(current === role)}
				>
					{role}
				</button>
			))}
			<button
				type="button"
				onClick={onCancel}
				className="cursor-pointer text-xs text-grey-500 hover:text-grey-900"
			>
				cancel
			</button>
		</div>
	)
}

type BooleanSettingRowProps = {
	label: string
	value: boolean | undefined
	disabled: boolean
	onSelect: (next: boolean) => void
}

function getToggleButtonClass(isActive: boolean): string {
	const base = 'rounded border px-2 py-0.5 text-xs cursor-pointer disabled:cursor-not-allowed disabled:opacity-50'
	return isActive
		? `${base} border-blue-500 bg-blue-50 text-blue-700`
		: `${base} border-grey-300 bg-white text-grey-700 hover:border-grey-500`
}

const CONNECTION_DOT: Record<EchoConnectionState, string> = {
	connected: 'bg-green-500',
	connecting: 'bg-amber-500',
	initialized: 'bg-amber-500',
	unavailable: 'bg-red-500',
	failed: 'bg-red-500',
	disconnected: 'bg-grey-400',
	uninitialized: 'bg-grey-300',
}

function ReverbStatusSection({ state }: { state: EchoConnectionState }) {
	return (
		<div className="flex items-center justify-between gap-4 border-t border-grey-200 px-5 py-3 text-sm text-grey-500">
			<span>Sockets</span>
			<span className="flex items-center gap-2 font-mono text-xs text-grey-700">
				<span className={`inline-block h-2 w-2 rounded-full ${CONNECTION_DOT[state]}`} />
				{state}
			</span>
		</div>
	)
}

function ReleaseSection() {
	const { data: deployedBuiltAt, dataUpdatedAt, errorUpdatedAt, isFetching, refetch } = useQuery({
		...deployedBuildQueryOptions,
		enabled: false,
	})
	const [now, setNow] = useState(() => Date.now())

	useEffect(() => {
		const interval = window.setInterval(() => setNow(Date.now()), 1000)
		return () => window.clearInterval(interval)
	}, [])

	const lastCheckedAt = Math.max(dataUpdatedAt, errorUpdatedAt)
	const runningBuiltAt = import.meta.env.VITE_APP_BUILT_AT
	const isOutdated = !!deployedBuiltAt && !!runningBuiltAt && deployedBuiltAt !== runningBuiltAt

	const rows: Array<[string, string | null]> = [
		['Running build', runningBuiltAt ?? null],
		['Latest release', deployedBuiltAt ?? null],
		['Last checked', lastCheckedAt > 0 ? formatDistanceStrict(lastCheckedAt, now, { addSuffix: true }) : null],
	]

	return (
		<div className="border-t border-grey-200 px-5 py-3 text-sm">
			<div className="flex items-center justify-between gap-4 text-grey-500">
				<span className="flex items-center gap-2">
					Release
					{deployedBuiltAt
						? (
							<span
								className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
									isOutdated ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
								}`}
							>
								{isOutdated ? 'new version available' : 'up to date'}
							</span>
						)
						: null}
				</span>
				<button
					type="button"
					onClick={() => void refetch()}
					disabled={isFetching}
					className="cursor-pointer text-xs text-grey-500 hover:text-grey-900 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{isFetching ? 'checking…' : 'check now'}
				</button>
			</div>
			<dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 font-mono text-xs">
				{rows.map(([key, value]) => (
					<div key={key} className="contents">
						<dt className="text-grey-500">{key}</dt>
						<dd className="break-all text-grey-900">
							{value ?? <span className="text-grey-400">—</span>}
						</dd>
					</div>
				))}
			</dl>
		</div>
	)
}

function BooleanSettingRow({ label, value, disabled, onSelect }: BooleanSettingRowProps) {
	return (
		<>
			<dt className="text-grey-500">{label}</dt>
			<dd className="break-all text-grey-900">
				<div className="flex items-center gap-2">
					{[true, false].map((option) => (
						<button
							key={String(option)}
							type="button"
							disabled={disabled || value === undefined}
							onClick={() => onSelect(option)}
							className={getToggleButtonClass(value === option)}
						>
							{String(option)}
						</button>
					))}
					{value === undefined && <span className="text-grey-400">—</span>}
				</div>
			</dd>
		</>
	)
}
